// Spatial helper for waypoint snap-to-track. Waypoints are absolute anchors and
// may be off-route; callers opt into snapping with an explicit threshold.
//
// Distance math uses a small equirectangular approximation: at the
// 1–10 km scales we care about (snap thresholds in metres) the error vs.
// haversine is well under a meter, and avoiding trig per-vertex keeps this
// hot path cheap even on tracks with 10 k points.

export interface TrackPointLite {
  lat: number
  lon: number
  ele?: number
}

export interface SnapResult {
  /** Snapped coordinates lying on the closest track segment. */
  lat: number
  lon: number
  /** Track elevation at the snapped point when available. */
  ele?: number
  /** Earth-surface distance from the input point to the snapped point. */
  distanceM: number
  /** Index of the nearest endpoint to the snapped point (useful for tests + debugging). */
  index: number
}

const EARTH_RADIUS_M = 6_371_000

/** Approximate metres-per-degree at the given latitude. The lon term
 *  collapses near the poles; for our latitudes (China / global trekking)
 *  this is accurate to <0.5 % which is fine inside metre-scale thresholds. */
function metersPerDegree(lat: number): { metersPerLat: number; metersPerLon: number } {
  const latRad = (lat * Math.PI) / 180
  const metersPerLat = (Math.PI / 180) * EARTH_RADIUS_M
  const metersPerLon = metersPerLat * Math.cos(latRad)
  return { metersPerLat, metersPerLon }
}

/** Find the nearest point on the track polyline to (lat, lon). Returns null
 *  only when the track is empty. */
export function nearestPointOnTrack(
  lat: number,
  lon: number,
  track: readonly TrackPointLite[],
): SnapResult | null {
  if (track.length === 0) return null
  const { metersPerLat, metersPerLon } = metersPerDegree(lat)
  if (track.length === 1) {
    const only = track[0]!
    const dLatM = (only.lat - lat) * metersPerLat
    const dLonM = (only.lon - lon) * metersPerLon
    return {
      lat: only.lat,
      lon: only.lon,
      ele: only.ele,
      distanceM: Math.hypot(dLatM, dLonM),
      index: 0,
    }
  }

  let best: SnapResult | null = null
  let bestSqMeters = Infinity
  for (let i = 0; i < track.length - 1; i += 1) {
    const a = track[i]!
    const b = track[i + 1]!
    const ax = (a.lon - lon) * metersPerLon
    const ay = (a.lat - lat) * metersPerLat
    const bx = (b.lon - lon) * metersPerLon
    const by = (b.lat - lat) * metersPerLat
    const vx = bx - ax
    const vy = by - ay
    const lenSq = vx * vx + vy * vy
    const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, -(ax * vx + ay * vy) / lenSq))
    const px = ax + vx * t
    const py = ay + vy * t
    const sq = px * px + py * py
    if (sq < bestSqMeters) {
      bestSqMeters = sq
      const snappedLat = lat + py / metersPerLat
      const snappedLon = lon + px / metersPerLon
      const ele =
        a.ele !== undefined && b.ele !== undefined
          ? a.ele + (b.ele - a.ele) * t
          : t <= 0.5
            ? a.ele
            : b.ele
      best = {
        lat: snappedLat,
        lon: snappedLon,
        ele,
        distanceM: Math.sqrt(sq),
        index: t <= 0.5 ? i : i + 1,
      }
    }
  }
  return best
}

/** Convenience wrapper that applies a threshold and returns `null`
 *  when the input is too far from the route. */
export const SNAP_THRESHOLD_M = 5

export function snapWithinThreshold(
  lat: number,
  lon: number,
  track: readonly TrackPointLite[],
  thresholdM: number = SNAP_THRESHOLD_M,
): SnapResult | null {
  const snapped = nearestPointOnTrack(lat, lon, track)
  if (!snapped) return null
  return snapped.distanceM <= thresholdM ? snapped : null
}
